import axios from "axios";
import moment from "moment-with-locales-es6";
import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import { DOCUMENT_TYPES, getDriverInfo } from "../../services/GetDriverInfo";

import { removeAccents } from "../../utils/string-utils";
import { DomainContext, mapDocumentType } from "../../domain-repository/DomainRepository";
import { driverInfoReducer } from "./driverInfoReducer";
import {
  checkDriverInfoStepEditMode,
  DriverFieldsErrors,
  filterCountries,
  mapCountryToOption,
  useFocusChain,
  useGetDriverInfo,
  validValidationObject,
} from "./driverInfoStepUtils";
import {
  useValidateTaxNumber,
  validateCountry,
  validateDate,
  validateDocumentID,
  validateNotEmpty,
} from "./driver-fields-validations";
import {
  ID_CARD_DOCUMENT_TYPE,
  isPortugalCountryId,
  isValidPtCitizenCardNumber,
  normalizePtCitizenCardNumber,
  PORTUGAL_COUNTRY_ID,
  PT_CITIZEN_CARD_EXAMPLE,
  useDebouncedValue,
} from "../../utils/utils";

import { AlertModalWithActions } from "../../components/alert-modal/AlertModal";
import DriverInfoDropdown from "./components/DriverInfoDropdown";
import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import Loading from "../../components/Loading";
import TrackingPage from "../../components/TrackingPage";
import withLang from "../../withLang";
import { DriverInfoPage } from "./components/DriverInfoPage";
import { LabeledDriverInfoInput } from "./components/DriverInfoInput";
import { Spacer } from "../../components/Spacer";
import DriverInfoOptionsInput from "./components/DriverInfoOptionsInput";
import DriverDateInput from "./components/DriverDateInput";
import { checkIfCountryHasNoEmissionDateLicenseApi } from "../../services/NoEmissionDateLicense";

export function getDriversCountryIdByName(countries, name) {
  const country = countries.find((c) => c.name === name);
  return country ? country.countryID : name;
}

function getDriversCountryNameById(countries, id) {
  const country = countries.find((c) => c.countryID === id);
  return country ? country.name : id;
}

/**
 * The portuguese citizen card format is only enforced when the document is a citizen card
 * issued in Portugal. Any other document keeps the generic validation.
 */
function isPtCitizenCardDocument(documentType, iDIssuedCountryID) {
  return (
    Number(documentType) === ID_CARD_DOCUMENT_TYPE &&
    isPortugalCountryId(iDIssuedCountryID)
  );
}

const FIELD_ORDER = [
  "country",
  "driversLicenceCountry",
  "docIdCountry",
  "taxNumber",
  "id_doc_type",
  "documentID",
  "iDExpireDay",
  "iDExpireMonth",
  "iDExpireYear",
];

function DriverStep1Page(props) {
  const { t, i18n } = useTranslation();
  const { registerRef, focusNext } = useFocusChain(FIELD_ORDER);

  const {
    addDriverInfo,
    setDriverInfo,
    driverInfo,
    getCountries,
    driverInfoHasClickedBack,
    setDriverInfoHasClickedBack,
    driverInfoEditMode,
    setDriverInfoEditMode,
    reservation,
    flowPreviousRoute,
    visitedRoutes,
    addVisitedRoutes,
    blockedTaxNumbersList,
  } = useContext(DomainContext);
  const safeReservation = reservation || {};

  const countries = useMemo(() => {
    const cs = getCountries();
    const ptCountry = cs.find((o) => o.countryID === PORTUGAL_COUNTRY_ID);
    return [
      ptCountry,
      ...cs.filter((o) => o.countryID !== PORTUGAL_COUNTRY_ID),
    ].filter((o) => o !== undefined);
  }, [getCountries]);

  const isInitialPtCitizenCard = isPtCitizenCardDocument(
    driverInfo.documentType,
    getDriversCountryIdByName(countries, driverInfo.iDIssuedPlace)
  );

  const initialDocumentID =
    (isInitialPtCitizenCard &&
      normalizePtCitizenCardNumber(driverInfo.documentID)) ||
    driverInfo.documentID ||
    "";

  // A citizen card number that comes wrongly filled from the reservation is flagged as soon as
  // the page opens, instead of waiting for the driver to click next.
  const hasInvalidInitialDocumentID =
    isInitialPtCitizenCard &&
    !!initialDocumentID &&
    !isValidPtCitizenCardNumber(initialDocumentID);

  const [state, dispatch] = useReducer(driverInfoReducer, {
    canDisplayErrors: false,
    country: driverInfo.countryID
      ? {
          value: driverInfo.countryID,
          label: getDriversCountryNameById(countries, driverInfo.countryID),
        }
      : null,
    driversLicenseIssuedPlace: driverInfo.driversLicenseIssuedPlace
      ? {
          value: getDriversCountryIdByName(
            countries,
            driverInfo.driversLicenseIssuedPlace
          ),
          label: driverInfo.driversLicenseIssuedPlace,
        }
      : null,
    iDIssuedPlace: driverInfo.iDIssuedPlace
      ? {
          value: getDriversCountryIdByName(countries, driverInfo.iDIssuedPlace),
          label: driverInfo.iDIssuedPlace,
        }
      : null,

    taxNumber: driverInfo.taxNumber || "",
    documentID: initialDocumentID,
    documentType: driverInfo.documentType || "",
    iDExpireDay:
      (driverInfo.iDExpireDate && moment(driverInfo.iDExpireDate).date()) || "",
    iDExpireMonth:
      (driverInfo.iDExpireDate &&
        moment(driverInfo.iDExpireDate).month() + 1) ||
      "",
    iDExpireYear:
      (driverInfo.iDExpireDate && moment(driverInfo.iDExpireDate).year()) || "",

    errorModal: null,
    isLoading: null,
  });

  const [validateCanSkip, setValidateCanSkip] = useState(
    !visitedRoutes.includes(window.location.pathname) && !driverInfoEditMode && !driverInfoHasClickedBack
  );

  const selectedCountry = useMemo(
    () => countries.find((c) => c.countryID === state.country?.value),
    [countries, state.country]
  );

  const taxNumberDebounced = useDebouncedValue(state.taxNumber, 600)
  const { data: taxNumberValidation, isLoading: isLoadingTaxNumberValidation } =
    useValidateTaxNumber(
      t,
      taxNumberDebounced,
      blockedTaxNumbersList,
      selectedCountry?.countryID
    );

  const countryValidation = useMemo(
    () => validateCountry(t, state.country?.label, countries),
    [countries, state.country, t]
  );

  const iDIssuedCountryValidation = useMemo(
    () => validateCountry(t, state.iDIssuedPlace?.label, countries),
    [countries, state.iDIssuedPlace, t]
  );

  const driversLicenseIssuedCountryValidation = useMemo(
    () => validateCountry(t, state.driversLicenseIssuedPlace?.label, countries),
    [countries, state.driversLicenseIssuedPlace, t]
  );

  const isPtCitizenCard = useMemo(
    () => isPtCitizenCardDocument(state.documentType, state.iDIssuedPlace?.value),
    [state.documentType, state.iDIssuedPlace]
  );

  const docIdDebounced = useDebouncedValue(state.documentID, 600)
  const documentIDValidation = useMemo(
    () => validateDocumentID(t, docIdDebounced, isPtCitizenCard),
    [docIdDebounced, isPtCitizenCard, t]
  );

  // The invalid number that came from the reservation is shown as an error until the driver starts
  // correcting it, moment from which the regular next click validation takes over.
  const canDisplayDocumentIDError =
    state.canDisplayErrors ||
    (hasInvalidInitialDocumentID && state.documentID === initialDocumentID);

  const getDriverInfoSuccessCB = useCallback((newDriverInfo) => {
    const docDate = newDriverInfo?.Document_Date ? moment(newDriverInfo.Document_Date) : null;
    dispatch({
      type: 'SET_VALUES',
      payload: {
        taxNumber: state.taxNumber || newDriverInfo?.Tax_Number || '',
        documentID: state.documentID || newDriverInfo?.Document_Number || '',
        documentType: state.documentType || mapDocumentType(newDriverInfo?.Document_Type) || '',
        iDExpireYear: docDate?.year() || '',
        iDExpireMonth: docDate ? docDate.month() + 1 : '',
        iDExpireDay: docDate?.date() || '',
      }
    });
  }, [t, state.taxNumber, state.documentID, state.documentType]);

  const { data: taxNumberDriverInfo, isLoading: isLoadingTaxNumberDriverInfo } = useGetDriverInfo(
    taxNumberDebounced,
    DOCUMENT_TYPES.TAX_NUMBER,
    selectedCountry?.countryID && isPortugalCountryId(selectedCountry?.countryID) && taxNumberValidation?.isValid,
    getDriverInfoSuccessCB
  )

  const { data: docIdDriverInfo, isLoading: isLoadingDocIdDriverInfo } = useGetDriverInfo(
    docIdDebounced,
    state.documentType,
    selectedCountry?.countryID && !isPortugalCountryId(selectedCountry?.countryID) && documentIDValidation?.isValid,
    getDriverInfoSuccessCB
  )

  const documentTypeValidation = useMemo(
    () => validateNotEmpty(t, state.documentType, "emptyDocumentType"),
    [state.documentType, t]
  );

  const iDExpireDateValidation = useMemo(
    () =>
      validateDate(
        t,
        state.iDExpireDay,
        state.iDExpireMonth,
        state.iDExpireYear,
        "invalidDriversIDExpireDate"
      ),
    [state.iDExpireDay, state.iDExpireMonth, state.iDExpireYear, t]
  );

  const canNext = useCallback(() => {
    return (
      countryValidation.isValid &&
      iDIssuedCountryValidation.isValid &&
      driversLicenseIssuedCountryValidation.isValid &&
      documentTypeValidation.isValid &&
      documentIDValidation.isValid &&
      taxNumberValidation?.isValid &&
      iDExpireDateValidation.isValid &&
      !isLoadingTaxNumberValidation &&
      !isLoadingTaxNumberDriverInfo &&
      !isLoadingDocIdDriverInfo
    );
  }, [
    countryValidation,
    iDIssuedCountryValidation,
    driversLicenseIssuedCountryValidation,
    selectedCountry,
    documentTypeValidation,
    documentIDValidation,
    iDExpireDateValidation,
    taxNumberValidation,
    isLoadingTaxNumberValidation,
    isLoadingTaxNumberDriverInfo,
    isLoadingDocIdDriverInfo
  ]);

  const countriesOptions = useMemo(() => {
    return countries.map(mapCountryToOption);
  }, [countries]);

  useEffect(() => {
    const editMode = checkDriverInfoStepEditMode(props.location.search);
    setDriverInfoEditMode(editMode);
  }, []);

  const handleProceedNextPage = useCallback(
    (type) => {
      function pushPage() {
        const url =
          type === "review" ? "/driver-info-review" : "/driver-info-step/2";
        setDriverInfoHasClickedBack(false);
        if (type === "redirect") {
          props.history.replace(url);
        } else {
          props.history.push(url);
        }
      }

      async function fun() {
        try {
          dispatch({ type: "SET_IS_LOADING", payload: true });

          const isCountryPortugal = isPortugalCountryId(
            selectedCountry?.countryID
          );

          const documentID =
            (isPtCitizenCard && normalizePtCitizenCardNumber(state.documentID)) ||
            state.documentID;

          if (driverInfoEditMode) {
            addDriverInfo({
              taxNumber: state.taxNumber,
              documentID,
              documentType: state.documentType,
              iDIssuedPlace: state.iDIssuedPlace.label,
              driversLicenseIssuedPlace: state.driversLicenseIssuedPlace.label,
              countryID: selectedCountry?.countryID,
              iDExpireDate: `${state.iDExpireYear}-${state.iDExpireMonth}-${state.iDExpireDay}`,
            });
            pushPage();
            return null;
          }

          setDriverInfo(
            {
              ...reservation.listReservation,
              DriversCountryID: selectedCountry?.countryID,
              DocumentIssuePlace: state.iDIssuedPlace.label,
              DriverLicensePlace: state.driversLicenseIssuedPlace.label,
              DriversTaxNumber: state.taxNumber,
              DocumentNumber: documentID,
              Document: state.documentType,
              DocumentDate: `${state.iDExpireYear}-${state.iDExpireMonth}-${state.iDExpireDay}`,
            },
            (isCountryPortugal ? taxNumberDriverInfo : docIdDriverInfo) || {}
          );

          pushPage();
        } catch (err) {
          setValidateCanSkip(false);

          dispatch({
            type: "SET_VALUES",
            payload: {
              isLoading: false,
              popupInfo: {
                isVisible: true,
                title: t("errorTitle"),
                message: t("errorMessage"),
                reason: "Erro ao obter informações do condutor.",
              },
            },
          });
        }
      }

      fun();
    },
    [
      countries,
      setDriverInfo,
      setDriverInfoHasClickedBack,
      selectedCountry,
      reservation.listReservation,
      state,
      props.history,
      t,
      taxNumberDriverInfo,
      docIdDriverInfo,
      isPtCitizenCard,
    ]
  );

  const onNextClick = useCallback(
    (type) => {
      const canSkipPage = canNext();
      dispatch({ type: "SET_VALUES", payload: { canDisplayErrors: true } });
      if (canSkipPage) {
        handleProceedNextPage(type);
      }
    },
    [handleProceedNextPage, canNext]
  );

  const onBack = useCallback(() => {
    flowPreviousRoute(props.history);
  }, [flowPreviousRoute, setDriverInfoHasClickedBack]);

  const onMountRef = React.useRef(false);
  useEffect(() => {
    if (
      !onMountRef.current &&
      taxNumberValidation !== undefined &&
      !visitedRoutes.includes(window.location.pathname) &&
      !driverInfoEditMode && !driverInfoHasClickedBack
    ) {
      onMountRef.current = true;
      const canSkipPage = canNext();
      if (canSkipPage) {
        handleProceedNextPage("redirect");
      } else {
        setValidateCanSkip(false);
        addVisitedRoutes(window.location.pathname);
      }
    }
  }, [taxNumberValidation, canNext, handleProceedNextPage]);

  const isLoading = state.isLoading || isLoadingTaxNumberDriverInfo || isLoadingDocIdDriverInfo

  const selectedLicenseCountry = useMemo(
    () => countries.find((c) => c.countryID === state.driversLicenseIssuedPlace?.value),
    [countries, state.driversLicenseIssuedPlace]
  );

  const checkIfCountryHasNoEmissionDateLicense = useCallback(async (countryID) => {
    const hasNoEmissionDateLicense = await checkIfCountryHasNoEmissionDateLicenseApi(countryID);
    addDriverInfo({
      hasNoEmissionDateLicense: hasNoEmissionDateLicense.isNonLicenseEmissionDate
    });
  }, []);
  
  useEffect(() => {
    if(selectedLicenseCountry) {
      checkIfCountryHasNoEmissionDateLicense(selectedLicenseCountry.countryID)
    }
  }, [selectedLicenseCountry])

  return (
    <DriverInfoPage isLoading={validateCanSkip}>
      <PageHeader
        title={t("personalInfo")}
        active={1}
        progress={"10%"}
        handleLogoClick={() => props.history.push("/start")}
      />
      <main className="page-layout">
        <div className="container grow">
          <Loading
            active={isLoading}
            message={t("validatingInformation")}
          />
          <div className="driver-content" style={{ height: "100%" }}>
            <div className="text-center driver-country-form__message">
              {t("checkInEndorseMessage", {
                pickupDate: moment(
                  safeReservation.PickupDate,
                  "YYYY-MM-DD HH:mm"
                )
                  .locale(i18n.language)
                  ?.format("D MMMM"),
              })}
            </div>
            <Spacer />
            <form>
              <DriverInfoDropdown
                selectRef={registerRef("country")}
                name="country"
                label={t("country")}
                placeholder={t("countryHelp")}
                search
                value={state.country || ""}
                options={countriesOptions}
                validationObject={
                  state.canDisplayErrors
                    ? countryValidation
                    : validValidationObject
                }
                onChange={(value) => {
                  dispatch({
                    type: "SET_VALUES",
                    payload: {
                      country: value,
                      driversLicenseIssuedPlace:
                        state.driversLicenseIssuedPlace || value,
                      iDIssuedPlace: state.iDIssuedPlace || value,
                    },
                  });
                  focusNext("country");
                }}
              />
              <Spacer />
              <DriverInfoDropdown
                selectRef={registerRef("driversLicenceCountry")}
                name="driversLicenceCountry"
                label={t("driversLicenseIssuedCountry")}
                placeholder={t("countryHelp")}
                search
                value={state.driversLicenseIssuedPlace || ""}
                options={countriesOptions}
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseIssuedCountryValidation
                    : validValidationObject
                }
                onChange={(value) => {
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseIssuedPlace: value },
                  });
                  focusNext("driversLicenceCountry");
                }}
              />
              <Spacer />
              <DriverInfoDropdown
                selectRef={registerRef("docIdCountry")}
                name="docIdCountry"
                label={t("iDIssuedCountry")}
                placeholder={t("countryHelp")}
                search
                value={state.iDIssuedPlace || ""}
                options={countriesOptions}
                validationObject={
                  state.canDisplayErrors
                    ? iDIssuedCountryValidation
                    : validValidationObject
                }
                onChange={(value) => {
                  dispatch({
                    type: "SET_VALUES",
                    payload: { iDIssuedPlace: value },
                  });
                  focusNext("docIdCountry");
                }}
              />
              <Spacer />
              <LabeledDriverInfoInput
                labelComp={
                  <span>
                    {t("taxNumber")}{" "}
                    {selectedCountry &&
                    !isPortugalCountryId(selectedCountry.countryID) ? (
                      <span className="optional-indication">
                        {" "}
                        ({t("optional")}){" "}
                      </span>
                    ) : null}
                  </span>
                }
                name="taxNumber"
                inputRef={registerRef("taxNumber")}
                autoComplete="off"
                value={state.taxNumber}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { taxNumber: value },
                  })
                }
                disabled={!state.country || !countryValidation.isValid}
                validationObject={
                  state.canDisplayErrors
                    ? taxNumberValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <div className="text-center">
                <label htmlFor="id_doc_type">{t("documentType")}</label>
                <DriverInfoOptionsInput
                  value={state.documentType}
                  onChange={(value) => {
                    dispatch({
                      type: "SET_VALUES",
                      payload: { documentType: value },
                    });
                    focusNext("id_doc_type");
                  }}
                  name="id_doc_type"
                  options={[
                    { value: 1, text: t("idCard") },
                    { value: 2, text: t("passport") },
                  ]}
                  error={!documentTypeValidation.isValid}
                />
              </div>
              <Spacer />
              <LabeledDriverInfoInput
                label={t("documentID")}
                name="documentID"
                inputRef={registerRef("documentID")}
                autoComplete="off"
                placeholder={isPtCitizenCard ? `${PT_CITIZEN_CARD_EXAMPLE}` : ""}
                value={state.documentID}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { documentID: value },
                  })
                }
                disabled={!state.country || !countryValidation.isValid}
                validationObject={
                  canDisplayDocumentIDError
                    ? documentIDValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <DriverDateInput
                t={t}
                label={t("iDExpireDate")}
                validationObject={
                  state.canDisplayErrors
                    ? iDExpireDateValidation
                    : validValidationObject
                }
                inputRef={registerRef("iDExpireDay")}
                dayValue={state.iDExpireDay}
                dayName="iDExpireDay"
                monthValue={state.iDExpireMonth}
                monthName="iDExpireMonth"
                yearValue={state.iDExpireYear}
                yearName="iDExpireYear"
                disabled={!state.country || !countryValidation.isValid}
                onDayChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { iDExpireDay: value },
                  })
                }
                onMonthChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { iDExpireMonth: value },
                  })
                }
                onYearChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { iDExpireYear: value },
                  })
                }
              />
            </form>
          </div>
        </div>
        <Footer
          prevHidden={driverInfoEditMode}
          handleNextClick={() => !isLoading && !isLoadingTaxNumberValidation && onNextClick()}
          handlePrevClick={onBack}
          nextHighlighted={canNext()}
        />
      </main>
    </DriverInfoPage>
  );
}

export default withLang(TrackingPage(DriverStep1Page, 2004));